import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { login, logout, register, type SessionResponse } from '../api/auth'
import { ApiError } from '../api/http'
import { connect } from '../api/trading'
import {
  listAvailablePlatforms,
  startPlatformOAuth,
  type PlatformDescriptor,
} from '../api/platforms'
import { useAuthStore } from '../auth/useAuthStore'

type AuthMode = 'login' | 'register'

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const body = JSON.parse(err.body) as { message?: string; upstream?: string }
      return body.upstream ? `${body.message ?? err.message}: ${body.upstream}` : body.message ?? err.message
    } catch {
      return err.body || err.message
    }
  }
  return err instanceof Error ? err.message : 'Unknown error'
}

export function UnlockScreen() {
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const vaultUnlocked = useAuthStore((s) => s.vaultUnlocked)
  const status = useAuthStore((s) => s.status)
  const errorMessage = useAuthStore((s) => s.errorMessage)
  const setSession = useAuthStore((s) => s.setSession)
  const setConnection = useAuthStore((s) => s.setConnection)
  const setStatus = useAuthStore((s) => s.setStatus)
  const setSkippedPlatformAttach = useAuthStore((s) => s.setSkippedPlatformAttach)
  const reset = useAuthStore((s) => s.reset)

  const [availablePlatforms, setAvailablePlatforms] = useState<PlatformDescriptor[]>([])
  const [selectedPlatformId, setSelectedPlatformId] = useState<string>('')
  const [credentialInputs, setCredentialInputs] = useState<Record<string, string>>({})

  // If the user is authenticated and the vault is unlocked, they belong on
  // the attach form — a fully connected user would already be in
  // ConnectedShell (App.tsx only renders UnlockScreen when status !==
  // 'connected'). Anything else (first run, "Add another" from Settings,
  // error after a failed attach attempt) keeps them here.
  const showAttach = isAuthenticated && vaultUnlocked

  useEffect(() => {
    if (!showAttach) return
    let cancelled = false
    listAvailablePlatforms()
      .then((res) => {
        if (cancelled) return
        setAvailablePlatforms(res.platforms)
        if (res.platforms[0]) setSelectedPlatformId(res.platforms[0].id)
      })
      .catch((err) => {
        console.warn('[platforms] failed to load available platforms for attach form', err)
      })
    return () => {
      cancelled = true
    }
  }, [showAttach])

  const selectedPlatform = useMemo(
    () => availablePlatforms.find((p) => p.id === selectedPlatformId) ?? null,
    [availablePlatforms, selectedPlatformId],
  )

  useEffect(() => {
    setCredentialInputs({})
  }, [selectedPlatformId])

  function applySession(session: SessionResponse) {
    setSession({
      user: session.user,
      isAuthenticated: session.authenticated,
      vaultUnlocked: session.vaultUnlocked,
    })
    setConnection(session.connection)
    if (!session.authenticated || !session.vaultUnlocked) {
      setStatus('unauthenticated')
    } else if (session.connection?.accountId) {
      setStatus('connected')
    } else {
      setStatus('needs_platform')
    }
  }

  async function handleAuthSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password) return
    if (mode === 'register' && password !== confirmPassword) {
      setStatus('error', 'Passwords do not match')
      return
    }
    setSubmitting(true)
    setStatus('authenticating')
    try {
      const session =
        mode === 'login'
          ? await login(email.trim(), password)
          : await register(email.trim(), password)
      applySession(session)
      setPassword('')
      setConfirmPassword('')
    } catch (err) {
      reset()
      setStatus('error', errorText(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAttachSubmit(e: FormEvent) {
    e.preventDefault()
    if (!selectedPlatform) return
    if (selectedPlatform.authType === 'oauth') {
      // OAuth attach is a redirect, not a form submit. Defer to the dedicated
      // handler so this code path never tries to read credential inputs.
      await handleOAuthStart()
      return
    }
    const credentials: Record<string, string> = {}
    for (const field of selectedPlatform.credentialFields) {
      const value = credentialInputs[field.key]?.trim() ?? ''
      if (!value) {
        setStatus('error', `${field.label} is required`)
        return
      }
      credentials[field.key] = value
    }
    setSubmitting(true)
    setStatus('connecting')
    try {
      await connect(selectedPlatform.id, credentials)
      setCredentialInputs({})
      // A real attachment supersedes any earlier skip — clear the flag so
      // the user gets the normal connected experience.
      setSkippedPlatformAttach(false)
      setStatus('connected')
    } catch (err) {
      setStatus('error', errorText(err))
    } finally {
      setSubmitting(false)
      setCredentialInputs({})
    }
  }

  async function handleOAuthStart() {
    if (!selectedPlatform) return
    const credentials: Record<string, string> = {}
    for (const field of selectedPlatform.credentialFields) {
      const value = credentialInputs[field.key]?.trim() ?? ''
      if (!value) {
        setStatus('error', `${field.label} is required`)
        return
      }
      credentials[field.key] = value
    }
    setSubmitting(true)
    setStatus('connecting')
    try {
      const { authorizeUrl } = await startPlatformOAuth(selectedPlatform.id, credentials)
      if (!authorizeUrl) throw new Error('Server did not return an authorize URL')
      window.location.href = authorizeUrl
    } catch (err) {
      setStatus('error', errorText(err))
      setSubmitting(false)
    }
  }

  async function handleLogout() {
    setSubmitting(true)
    try {
      await logout()
    } finally {
      reset()
      setCredentialInputs({})
      setSubmitting(false)
    }
  }

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg p-6 shadow-xl">
          <h1 className="text-2xl font-semibold text-zinc-100 mb-1">Trading Console</h1>
          <p className="text-sm text-zinc-400">Restoring your session…</p>
        </div>
      </div>
    )
  }

  const isOAuthPlatform = selectedPlatform?.authType === 'oauth'
  const allCredentialsFilled =
    selectedPlatform != null &&
    selectedPlatform.credentialFields.every(
      (f) => (credentialInputs[f.key] ?? '').trim().length > 0,
    )

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg p-6 shadow-xl">
        <h1 className="text-2xl font-semibold text-zinc-100 mb-1">Trading Console</h1>

        {showAttach ? (
          <form onSubmit={handleAttachSubmit}>
            <div className="flex items-start justify-between gap-4 mb-6">
              <p className="text-sm text-zinc-400">
                Connect a trading platform to this local account. Your credentials are encrypted
                with your password-unlocked vault before they are stored.
              </p>
              <button
                type="button"
                onClick={handleLogout}
                disabled={submitting}
                className="text-xs text-zinc-400 hover:text-zinc-100 disabled:text-zinc-600"
              >
                Sign out
              </button>
            </div>

            <div className="mb-4 rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
              <div className="text-xs text-zinc-500">SIGNED IN</div>
              <div className="text-sm text-zinc-200 truncate">{user?.email}</div>
            </div>

            {availablePlatforms.length === 0 ? (
              <div className="text-sm text-zinc-400">Loading platforms…</div>
            ) : (
              <>
                {availablePlatforms.length > 1 && (
                  <>
                    <label className="block text-xs font-medium text-zinc-400 mb-2">PLATFORM</label>
                    <select
                      value={selectedPlatformId}
                      onChange={(e) => setSelectedPlatformId(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 text-sm mb-4 focus:outline-none focus:border-emerald-500"
                    >
                      {availablePlatforms.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.displayName}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {availablePlatforms.length === 1 && selectedPlatform && (
                  <div className="mb-4 text-xs text-zinc-500">
                    Connecting to{' '}
                    <span className="text-zinc-300">{selectedPlatform.displayName}</span>
                  </div>
                )}

                {isOAuthPlatform && (
                  <div className="mb-4 rounded border border-zinc-800 bg-zinc-950 px-3 py-3 text-xs text-zinc-400 leading-relaxed">
                    Paste the App Key and App Secret from your own Schwab developer app. Make
                    sure your app's Callback URL is set to{' '}
                    <code className="text-zinc-200 break-all">
                      {selectedPlatform?.oauthRedirectUri ?? '(server has no SCHWAB_REDIRECT_URI set)'}
                    </code>
                    . After saving, you'll be redirected to Schwab to sign in and pick which
                    accounts to link. Refresh tokens expire every 7 days.
                  </div>
                )}
                {selectedPlatform?.credentialFields.map((field, idx) => (
                  <div key={field.key} className={idx > 0 ? 'mt-4' : ''}>
                    <label className="block text-xs font-medium text-zinc-400 mb-2 flex items-center justify-between">
                      <span>{field.label.toUpperCase()}</span>
                      {field.helpUrl && (
                        <a
                          href={field.helpUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-zinc-500 hover:text-emerald-400 normal-case font-normal"
                        >
                          where do I find this?
                        </a>
                      )}
                    </label>
                    <input
                      type={field.type === 'password' ? 'password' : 'text'}
                      autoFocus={idx === 0}
                      value={credentialInputs[field.key] ?? ''}
                      onChange={(e) =>
                        setCredentialInputs((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      placeholder={field.placeholder}
                      className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 font-mono text-sm focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                ))}
              </>
            )}

            {errorMessage && (
              <div className="mt-4 text-sm text-red-400 bg-red-950/40 border border-red-900 rounded p-3 whitespace-pre-wrap break-all">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !allCredentialsFilled}
              className="mt-6 w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-400 text-white font-medium py-2 rounded transition"
            >
              {submitting
                ? isOAuthPlatform
                  ? 'Redirecting…'
                  : 'Connecting...'
                : isOAuthPlatform
                  ? `Connect with ${selectedPlatform?.displayName ?? 'platform'}`
                  : 'Connect'}
            </button>

            <button
              type="button"
              onClick={() => {
                setSkippedPlatformAttach(true)
                setStatus('connected')
              }}
              disabled={submitting}
              className="mt-3 w-full text-sm text-zinc-400 hover:text-zinc-100 disabled:text-zinc-600 py-2"
            >
              Skip for now
            </button>
          </form>
        ) : (
          <form onSubmit={handleAuthSubmit}>
            <p className="text-sm text-zinc-400 mb-6">
              Sign in to unlock encrypted platform details stored in the local SQLite database.
            </p>

            <div className="grid grid-cols-2 gap-2 mb-5">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`rounded border px-3 py-2 text-sm transition ${
                  mode === 'login'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100'
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                className={`rounded border px-3 py-2 text-sm transition ${
                  mode === 'register'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100'
                }`}
              >
                Register
              </button>
            </div>

            <label className="block text-xs font-medium text-zinc-400 mb-2">EMAIL</label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500"
            />

            <label className="block text-xs font-medium text-zinc-400 mt-4 mb-2">PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500"
            />

            {mode === 'register' && (
              <>
                <label className="block text-xs font-medium text-zinc-400 mt-4 mb-2">
                  CONFIRM PASSWORD
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-emerald-500"
                />
              </>
            )}

            {errorMessage && (
              <div className="mt-4 text-sm text-red-400 bg-red-950/40 border border-red-900 rounded p-3 whitespace-pre-wrap break-all">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={
                submitting ||
                !email.trim() ||
                !password ||
                (mode === 'register' && !confirmPassword)
              }
              className="mt-6 w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-400 text-white font-medium py-2 rounded transition"
            >
              {submitting
                ? mode === 'login'
                  ? 'Signing in...'
                  : 'Creating account...'
                : mode === 'login'
                  ? 'Sign in'
                  : 'Create account'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
